import { useCallback, useEffect, useMemo, useRef } from "react";
import { Box } from "@hightouch/htui";
import { transformHtmlForDarkMode } from "@hightouch/lib/llm/lifecycle-studio/email-png-export/dark-mode-transform";
import { DARK_BACKGROUND } from "@hightouch/lib/llm/lifecycle-studio/email-png-export/dark-mode";
import { injectBlockScriptsMeta } from "src/components/preview-canvas/email-preview-security";

import { EDITOR_STYLE_ATTRIBUTE } from "./inject-document-style";

/** "retarget": links open in a new tab (the full preview). "disable": links
 * don't navigate — for previews on a canvas where a click means "select this
 * element" rather than "follow this link". */
export type EmailPreviewLinkBehavior = "retarget" | "disable";

/**
 * Adds target="_blank" and rel="noopener noreferrer" to anchor tags so links
 * open in a new tab instead of navigating the preview iframe. `#` links stay
 * in the frame so in-page anchors scroll.
 */
export function retargetEmailLinks(html: string): string {
  return html.replace(
    /<a\s(?![^>]*\bhref\s*=\s*["']?#)/gi,
    '<a target="_blank" rel="noopener noreferrer" ',
  );
}

/**
 * Stop links from navigating WITHOUT touching `pointer-events`.
 *
 * The CSS approach (`a { pointer-events: none }`) also makes the anchor's whole
 * SUBTREE untargetable, and `pointer-events` inherits — so on a canvas where a
 * click selects an element, nothing inside a link can be selected or hovered.
 * An email that wraps an entire card in one `<a>` loses all of it.
 *
 * Blocking the event leaves hit testing and `:hover` intact. This is the same
 * mechanism the LCM Edit tab's canvas uses (see `use-iframe-interactivity`),
 * where `mousedown` is deliberately left alone — preventDefault there blocks
 * native caret placement.
 */
export function blockLinkNavigation(doc: Document): () => void {
  const block = (event: MouseEvent) => {
    if ((event.target as Element | null)?.closest("a")) {
      event.preventDefault();
    }
  };
  doc.addEventListener("click", block);
  // Middle-click never fires `click`.
  doc.addEventListener("auxclick", block);
  return () => {
    doc.removeEventListener("click", block);
    doc.removeEventListener("auxclick", block);
  };
}

/** A srcDoc frame resolves `#id` against the app's URL, so following it would load the app in the frame; scroll instead. */
export function scrollInPageAnchors(doc: Document): () => void {
  const scroll = (event: MouseEvent) => {
    const href = (event.target as Element | null)
      ?.closest("a")
      ?.getAttribute("href");
    if (!href?.startsWith("#")) return;
    event.preventDefault();
    const id = href.slice(1);
    const target =
      doc.getElementById(id) ??
      (id === "" || id.toLowerCase() === "top" ? doc.documentElement : null);
    target?.scrollIntoView();
  };
  doc.addEventListener("click", scroll);
  return () => doc.removeEventListener("click", scroll);
}

// Cancel form events in every preview; form submission is never a preview interaction.
export function blockFormSubmission(doc: Document): () => void {
  const block = (event: Event) => event.preventDefault();
  doc.addEventListener("submit", block, true);
  return () => doc.removeEventListener("submit", block, true);
}

// Zero the UA's default body margin (8px) — it pushes full-width emails past the
// iframe (horizontal scrollbar) and offsets content below the measured height
// (vertical scrollbar) — and hide any residual scrollbar. Only used when the
// container is sized to the content (fitToContent), never in the scrollable
// full preview.
//
// `display:flow-root` makes body a block formatting context so its children's
// vertical margins are CONTAINED instead of collapsing through it. Without it,
// content whose root carries a margin (`<p style="margin:6px 0 0 0">`, the shape
// most of our text blocks have) renders that margin outside the body box, so
// `scrollHeight` under-reports by exactly the margin and the container we size
// from it clips the bottom of the content. Same rendered position either way —
// the margin just becomes measurable.
//
// The inset pads `html` because an email's inline `<body style="padding:0!important">` beats any sheet.
// `border-box` keeps it inside an email-forced `height:100%`, which would otherwise grow every measure.
export const fitToContentStyle = (contentPaddingPx: number) =>
  `<style ${EDITOR_STYLE_ATTRIBUTE}="">html{margin:0 !important;padding:${contentPaddingPx}px !important;box-sizing:border-box !important;}` +
  "body{margin:0 !important;padding:0 !important;}" +
  "body{display:flow-root;}" +
  "html{scrollbar-width:none !important;}" +
  "html::-webkit-scrollbar,body::-webkit-scrollbar{width:0 !important;height:0 !important;display:none !important;}</style>";

/**
 * The email's content height. The body's own box is the primary read — it
 * tracks content height for table-based email flow even when the iframe is
 * taller, where documentElement would report the viewport. Floored, because a
 * container a fraction taller than its content shows a hairline of body
 * background under content that runs flush to the bottom.
 *
 * Overflow means the body's height was FORCED viewport-tall — under
 * `display:flow-root` block flow contains its children, so it can't overflow
 * on its own. `html,body{height:100% !important}` (the standard email reset)
 * does exactly that, and the body then measures the iframe, not the email.
 * Reading the box there is a shrinking loop: the container sized from it is
 * border-box with a hairline border, so the next read is 2px shorter, once per
 * ResizeObserver tick, until the email collapses.
 */
export function resolveContentHeight(
  rectHeight: number,
  scrollHeight: number,
): number {
  const rect = Math.floor(rectHeight);
  // A pixel of slack: an unforced body can report a scrollHeight one pixel
  // over its floored box purely from rounding, which is not an overflow.
  return scrollHeight > rect + 1 ? scrollHeight : rect;
}

type EmailPreviewIframeProps = {
  html: string;
  isDarkMode?: boolean;
  linkBehavior?: EmailPreviewLinkBehavior;
  /** Reports the rendered email's content height (px). Fires on load and on
   * later reflow (web fonts, images). Use it to size a container to the full
   * email. */
  onMeasureHeight?: (height: number) => void;
  /** Reports the laid-out document width (px) once per load, at the width the
   * iframe loaded with. It exceeds that width when content such as a no-wrap
   * cell cannot shrink to fit. */
  onMeasureWidth?: (width: number) => void;
  /** The container is sized to the email's content height (canvas node): zero
   * the default body margin and hide scrollbars so the email sits flush with no
   * inner scroll. Leave off for the scrollable full preview. */
  fitToContent?: boolean;
  /** Room on every side for chrome a caller paints outside an element's box
   * (rings, badges). Included in the measured height; the caller widens its
   * container by twice this, or a fixed-width email overflows. `fitToContent`
   * only. */
  contentPaddingPx?: number;
  /** Called on each load with the rendered document, for callers that attach
   * interactivity (e.g. element selection). Return a teardown to run before the
   * next load and on unmount. */
  onDocumentReady?: (doc: Document) => (() => void) | void;
  title?: string;
  width?: string | number;
  maxWidth?: string | number;
};

/**
 * The email preview iframe primitive: builds the srcDoc (dark-mode transform +
 * link handling) and, optionally, measures the content height. Shared by the
 * LCM full-preview `EmailRenderer`, the LCM dynamic-content canvas nodes, and
 * the ND dynamic-content canvas so all render identically. Fills its container
 * (`height: 100%`) — the caller sizes the wrapper (e.g. from
 * `onMeasureHeight`).
 */
export const EmailPreviewIframe = ({
  html,
  isDarkMode = false,
  linkBehavior = "retarget",
  onMeasureHeight,
  onMeasureWidth,
  fitToContent = false,
  contentPaddingPx = 0,
  onDocumentReady,
  title = "Email preview",
  width = "100%",
  maxWidth = "100%",
}: EmailPreviewIframeProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const readyTeardownRef = useRef<(() => void) | null>(null);
  const linkGuardTeardownRef = useRef<(() => void) | null>(null);
  const formGuardTeardownRef = useRef<(() => void) | null>(null);

  const srcDoc = useMemo(() => {
    let result = linkBehavior === "disable" ? html : retargetEmailLinks(html);
    if (isDarkMode) result = transformHtmlForDarkMode(result);
    result = injectBlockScriptsMeta(result);
    if (fitToContent) result += fitToContentStyle(contentPaddingPx);
    return result;
  }, [html, isDarkMode, linkBehavior, fitToContent, contentPaddingPx]);

  const measure = useCallback(() => {
    const body = iframeRef.current?.contentDocument?.body;
    if (!body) return;
    const measured = resolveContentHeight(
      body.getBoundingClientRect().height,
      body.scrollHeight,
    );
    if (measured > 0) onMeasureHeight?.(measured + 2 * contentPaddingPx);
  }, [onMeasureHeight, contentPaddingPx]);

  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    // Read once and never from the resize observer: a caller that widens the
    // iframe to this value would otherwise grow viewport-relative content forever.
    const measuredWidth = doc?.documentElement.scrollWidth ?? 0;
    if (measuredWidth > 0) onMeasureWidth?.(measuredWidth);
    if (onMeasureHeight && doc?.body) {
      measure();
      observerRef.current?.disconnect();
      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(() => measure());
        observer.observe(doc.body);
        observerRef.current = observer;
      }
    }
    // Re-installed per load: a srcDoc change reloads the document, so the
    // listeners have to be re-attached to the new one (and detached from the old).
    linkGuardTeardownRef.current?.();
    linkGuardTeardownRef.current = !doc
      ? null
      : linkBehavior === "disable"
        ? blockLinkNavigation(doc)
        : scrollInPageAnchors(doc);
    formGuardTeardownRef.current?.();
    formGuardTeardownRef.current = doc ? blockFormSubmission(doc) : null;
    if (onDocumentReady && doc) {
      readyTeardownRef.current?.();
      readyTeardownRef.current = onDocumentReady(doc) ?? null;
    }
  }, [measure, onMeasureHeight, onMeasureWidth, onDocumentReady, linkBehavior]);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      readyTeardownRef.current?.();
      linkGuardTeardownRef.current?.();
      formGuardTeardownRef.current?.();
    },
    [],
  );

  return (
    <Box
      as="iframe"
      ref={iframeRef}
      title={title}
      srcDoc={srcDoc}
      onLoad={handleLoad}
      width={width}
      maxWidth={maxWidth}
      height="100%"
      border="none"
      bg={isDarkMode ? DARK_BACKGROUND : "white"}
      sx={{ colorScheme: isDarkMode ? "dark" : "light" }}
    />
  );
};
